"use client";
import React, { FC } from "react";
import Image from "next/image";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import styles from "./imageTextComponent.module.css";

export type ImageTextComponentProps = {
  layout: "text-left-image-right" | "text-right-image-left" | "text-top-image-bottom" | "image-top-text-bottom";
  heading?: string;
  boldParagraph?: string;
  regularParagraph?: string;
  imageUrl?: string;
  imageAlt?: string;
};

const ImageTextComponent: FC<ImageTextComponentProps> = ({
  layout,
  heading = "",
  boldParagraph,
  regularParagraph,
  imageUrl = "",
  imageAlt = "Content image",
}) => {
  const [sectionRef, isVisible] = useScrollAnimation();

  // Don't render if essential props are missing
  if (!heading || !imageUrl) {
    return null;
  }
  
  // Determine if horizontal or vertical layout
  const isHorizontal = layout === "text-left-image-right" || layout === "text-right-image-left";
  const isTextFirst = layout === "text-left-image-right" || layout === "text-top-image-bottom";

  // Text content component
  const TextContent = () => (
    <article className="flex flex-col gap-4 w-full">
      <div className="flex items-center gap-3">
        <div className="w-1 h-8 bg-cyan-400 flex-shrink-0" aria-hidden="true" />
        <h2 className="font-assistant font-semibold text-white text-[32px] tracking-[0] leading-6">
          {heading}
        </h2>
      </div>
      {boldParagraph && (
        <p className="font-inter font-bold text-white text-base tracking-[0] leading-6">
          {boldParagraph}
        </p>
      )}
      {regularParagraph && (
        <p className="font-inter font-normal text-slate-300 text-base tracking-[0] leading-6">
          {regularParagraph}
        </p>
      )}
    </article>
  );

  // Image content component - accepts alignment prop
  const ImageContent = ({ align = "center" }: { align?: "left" | "right" | "center" }) => {
    const justifyClass = align === "left" ? "justify-start" : align === "right" ? "justify-end" : "justify-center";
    return (
      <div className={`relative w-full flex items-center ${justifyClass}`}>
        <div className="relative w-full max-w-[500px]">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={1020}
            height={367}
            className="w-full h-auto object-contain"
            priority
          />
        </div>
      </div>
    );
  };

  if (isHorizontal) {
    return (
      <section
        ref={sectionRef as React.RefObject<HTMLElement>}
        className={`w-full py-12 px-4 md:px-8 ${isVisible ? styles.sectionAnimation : ""}`}
        aria-label="Image and text content section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-[50px]">
            {isTextFirst ? (
              <>
                <div className={`w-full md:w-[536px] flex-shrink-0 ${isVisible ? styles.textAnimation : ""}`}>
                  <TextContent />
                </div>
                <div className={`w-full md:flex-1 flex items-center ${isVisible ? styles.imageAnimation : ""}`}>
                  <ImageContent align="right" />
                </div>
              </>
            ) : (
              <>
                <div className={`w-full md:flex-1 flex items-center order-2 md:order-1 ${isVisible ? styles.imageAnimation : ""}`}>
                  <ImageContent align="left" />
                </div>
                <div className={`w-full md:w-[536px] flex-shrink-0 order-1 md:order-2 ${isVisible ? styles.textAnimation : ""}`}>
                  <TextContent />
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Vertical layouts
  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      className={`w-full py-12 px-4 md:px-8 ${isVisible ? styles.sectionAnimation : ""}`}
      aria-label="Image and text content section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col gap-12">
          {isTextFirst ? (
            <>
              <div className={isVisible ? styles.textAnimation : ""}>
                <TextContent />
              </div>
              <div className={isVisible ? styles.imageAnimation : ""}>
                <ImageContent />
              </div>
            </>
          ) : (
            <>
              <div className={isVisible ? styles.imageAnimation : ""}>
                <ImageContent />
              </div>
              <div className={isVisible ? styles.textAnimation : ""}>
                <TextContent />
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ImageTextComponent;

